/**
 * The signature's fields, opened in the letter itself: a click on the
 * signature under the text turns it into this, and Save turns it back.
 *
 * It edits the SAVED signature, not a copy for this one letter: the next
 * letter starts from the same words, and there is one place where a
 * signature is true. A writer still on the shared signatures, or on none, gets
 * their own first one here, filled in from the account.
 *
 * Escape cancels here and goes no further: in the composer it would otherwise
 * close the letter.
 */
import { useEffect, useState } from "react";
import { LoadingState } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getMyMailSettings, saveMyMailSettings, type MyMailSettings, type OwnSignature } from "../../../../lib/mailSignature";
import { Btn, useToast } from "../../../ui";
import { useCrmUser } from "../../../shell/CrmUser";
import { Note } from "../../settings/panel/fields";
import { SignatureFields } from "./SignatureFields";
import { settleDefault, starterSignature } from "./lib";

export function InlineSignatureEditor({
  editId,
  onDone,
}: {
  /** The own signature to change, or null to make a first one. */
  editId: string | null;
  /** Called with the saved signature's id, or null when cancelled. */
  onDone: (savedId: string | null) => void;
}) {
  const t = useT();
  const toast = useToast();
  const user = useCrmUser();
  const [view, setView] = useState<MyMailSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [sig, setSig] = useState<OwnSignature | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getMyMailSettings()
      .then((v) => {
        if (!alive) return;
        setView(v);
        const mine = editId ? v.signatures.find((s) => s.id === editId) : undefined;
        setSig(
          mine ??
            starterSignature({
              label: v.signatures.length === 0 ? t("myMail.starterLabel") : "",
              closing: t("myMail.starterClosing"),
              name: user.name || "",
              card: Boolean(v.company),
            }),
        );
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
    // Once per opening: what is being edited does not change while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isNew = Boolean(view && sig && !view.signatures.some((s) => s.id === sig.id));

  async function save() {
    if (!view || !sig) return;
    setBusy(true);
    try {
      const rows = isNew ? [...view.signatures, sig] : view.signatures.map((s) => (s.id === sig.id ? sig : s));
      // A first signature becomes the default; otherwise the default stays.
      const defaultId = view.signatures.length === 0 ? sig.id : settleDefault(rows, view.defaultId);
      await saveMyMailSettings({ signatures: rows, defaultId, fromAddress: view.fromAddress });
      toast(t("myMail.saved"));
      onDone(sig.id);
    } catch {
      toast(t("myMail.saveFailed"));
      setBusy(false);
    }
  }

  return (
    <div
      className="space-y-3 rounded-2xl border border-[var(--crm-hair)] p-3.5"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onDone(null);
      }}
    >
      {failed ? (
        <Note tone="bad">{t("myMail.loadFailed")}</Note>
      ) : !view || !sig ? (
        <LoadingState variant="inline" label={t("common.loading")} />
      ) : (
        <>
          <Note tone="info">{isNew ? t("myMail.inlineNew") : t("myMail.inlineNote")}</Note>
          <SignatureFields
            sig={sig}
            company={view.company}
            limits={view.limits}
            onChange={(part) => setSig((cur) => (cur ? { ...cur, ...part } : cur))}
            autoFocus
          />
        </>
      )}
      {/* Only once the fields are there. While they load, the buttons sat
          where the signature had been, and the second click of a quick double
          click on the signature landed on Cancel. */}
      {view && sig ? (
        <div className="flex gap-2">
          <Btn small type="button" tone="dark" disabled={busy} onClick={() => void save()}>
            {t("common.save")}
          </Btn>
          <Btn small type="button" onClick={() => onDone(null)}>
            {t("common.cancel")}
          </Btn>
        </div>
      ) : failed ? (
        <Btn small type="button" onClick={() => onDone(null)}>
          {t("common.close")}
        </Btn>
      ) : null}
    </div>
  );
}
